import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronDown, ChevronUp, Check, X } from 'lucide-react';
import { getFaqKey } from '../utils/keys';

const USStudentVisaPage = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { question: t('usStudent_faq1Q'), answer: t('usStudent_faq1A') },
    { question: t('usStudent_faq2Q'), answer: t('usStudent_faq2A') },
    { question: t('usStudent_faq3Q'), answer: t('usStudent_faq3A') },
    { question: t('usStudent_faq4Q'), answer: t('usStudent_faq4A') },
    { question: t('usStudent_faq5Q'), answer: t('usStudent_faq5A') },
    { question: t('usStudent_faq6Q'), answer: t('usStudent_faq6A') },
  ];

  const steps = [
    { title: t('usStudent_step1Title'), desc: t('usStudent_step1Desc') },
    { title: t('usStudent_step2Title'), desc: t('usStudent_step2Desc') },
    { title: t('usStudent_step3Title'), desc: t('usStudent_step3Desc') },
    { title: t('usStudent_step4Title'), desc: t('usStudent_step4Desc') },
    { title: t('usStudent_step5Title'), desc: t('usStudent_step5Desc') },
    { title: t('usStudent_step6Title'), desc: t('usStudent_step6Desc') },
    { title: t('usStudent_step7Title'), desc: t('usStudent_step7Desc') },
    { title: t('usStudent_step8Title'), desc: t('usStudent_step8Desc') },
  ];

  const documents = [
    t('usStudent_doc1'), t('usStudent_doc2'), t('usStudent_doc3'),
    t('usStudent_doc4'), t('usStudent_doc5'), t('usStudent_doc6'),
    t('usStudent_doc7'), t('usStudent_doc8'), t('usStudent_doc9'),
  ];

  const features = [
    { label: t('usStudent_feat_i20'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_sevis'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_ds160'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_appointment'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_docChecklist'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_interviewTips'), basic: true, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_specialist'), basic: false, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_financial'), basic: false, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_mockInterview'), basic: false, standard: true, premium: true, vip: true },
    { label: t('usStudent_feat_sop'), basic: false, standard: false, premium: true, vip: true },
    { label: t('usStudent_feat_seniorConsultant'), basic: false, standard: false, premium: true, vip: true },
    { label: t('usStudent_feat_courier'), basic: false, standard: false, premium: true, vip: true },
    { label: t('usStudent_feat_priority'), basic: false, standard: false, premium: false, vip: true },
    { label: t('usStudent_feat_postVisa'), basic: false, standard: false, premium: false, vip: true },
    { label: t('usStudent_feat_247support'), basic: false, standard: false, premium: false, vip: true },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const StatusIcon = ({ enabled }) =>
    enabled ? (
      <Check className="w-5 h-5 text-green-600 mx-auto" />
    ) : (
      <X className="w-5 h-5 text-gray-400 mx-auto" />
    );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative h-[500px] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/d25227ee-79aa-4fd4-96e0-b9726e9ae585/images/6dbc4c015c425af31f8983b0fe66674c7a6195c4948cc0064d84102ecf93e481.png)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            <div className="inline-block bg-yellow-400 text-black px-4 py-2 rounded font-bold mb-4" data-testid="usstudent-hero-badge">
              {t('usStudent_heroBadge')}
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="usstudent-hero-title">
              {t('usStudent_heroTitle')}
            </h1>
            <p className="text-xl text-white/90 mb-8" data-testid="usstudent-hero-desc">
              {t('usStudent_heroDesc')}
            </p>
            <a
              href="https://wa.me/971585862103?text=Hello%2C%20I%20would%20like%20to%20apply%20for%20a%20US%20F1%20Student%20Visa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all"
              data-testid="usstudent-apply-btn"
            >
              {t('usStudent_applyNow')}
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-16" data-testid="usstudent-intro">
          <p className="text-gray-700 text-lg leading-relaxed">
            {t('usStudent_intro1_part1')}
            <strong>{t('usStudent_intro1_bold1')}</strong>
            {t('usStudent_intro1_part2')}
          </p>
          <p className="text-gray-700 text-lg leading-relaxed mt-4">
            {t('usStudent_intro2_part1')}
            <strong>{t('usStudent_intro2_bold1')}</strong>
            {t('usStudent_intro2_part2')}
          </p>
        </div>

        {/* Process Steps */}
        <div className="mb-16" data-testid="usstudent-process">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('usStudent_processTitle')}</h2>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div className="flex gap-6" key={`usstudent-step-${index}`}>
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-logo-blue rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {index + 1}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-600 mt-8 text-center">{t('usStudent_processNote')}</p>
        </div>

        {/* Documents Section */}
        <div className="mb-16" data-testid="usstudent-documents">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('usStudent_docsTitle')}</h3>
          <ul className="space-y-3 text-gray-700">
            {documents.map((doc, index) => (
              <li className="flex items-start" key={`usstudent-doc-${index}`}>
                <span className="mr-2">&bull;</span>
                <span>{doc}</span>
              </li>
            ))}
          </ul>
          <p className="text-gray-600 mt-6 italic">{t('usStudent_docsNote')}</p>
        </div>

        {/* FAQ Section */}
        <div className="mb-16" data-testid="usstudent-faq">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('usStudent_faqTitle')}</h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={getFaqKey(faq.question, index)} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-4 text-left bg-white hover:bg-gray-50 transition-colors"
                  data-testid={`usstudent-faq-btn-${index}`}
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-logo-blue flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Packages Table */}
        <div className="mb-16" data-testid="usstudent-packages">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('usStudent_packagesTitle')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">{t('usStudent_tableFeatures')}</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('usStudent_basicStudent')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('usStudent_basicDesc')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900 bg-blue-50">
                    <div>{t('usStudent_standardStudent')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('usStudent_standardDesc')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('usStudent_premiumStudent')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('usStudent_premiumDesc')}</div>
                  </th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">
                    <div>{t('usStudent_vipStudent')}</div>
                    <div className="text-sm font-normal text-gray-600">{t('usStudent_vipDesc')}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {features.map((feat, idx) => (
                  <tr key={`usstudent-feat-${idx}`} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                    <td className="border border-gray-300 p-3 text-gray-700">{feat.label}</td>
                    <td className="border border-gray-300 p-3 text-center"><StatusIcon enabled={feat.basic} /></td>
                    <td className="border border-gray-300 p-3 text-center bg-blue-50"><StatusIcon enabled={feat.standard} /></td>
                    <td className="border border-gray-300 p-3 text-center"><StatusIcon enabled={feat.premium} /></td>
                    <td className="border border-gray-300 p-3 text-center"><StatusIcon enabled={feat.vip} /></td>
                  </tr>
                ))}
                <tr>
                  <td className="border border-gray-300 p-4"></td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Basic%20F1%20Student%20Visa%20package" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">
                      {t('usStudent_getInTouch')}
                    </a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center bg-blue-50">
                    <a href="https://wa.me/971585862103?text=I%20want%20Standard%20F1%20Student%20Visa%20package" target="_blank" rel="noopener noreferrer" className="inline-block bg-gradient-to-r from-logo-blue to-logo-cyan hover:shadow-lg text-white px-6 py-2 rounded font-semibold transition-all">
                      {t('usStudent_getInTouch')}
                    </a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20Premium%20F1%20Student%20Visa%20package" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">
                      {t('usStudent_getInTouch')}
                    </a>
                  </td>
                  <td className="border border-gray-300 p-4 text-center">
                    <a href="https://wa.me/971585862103?text=I%20want%20VIP%20F1%20Student%20Visa%20package" target="_blank" rel="noopener noreferrer" className="inline-block bg-gray-200 hover:bg-gray-300 text-gray-900 px-6 py-2 rounded font-semibold transition-colors">
                      {t('usStudent_getInTouch')}
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>

      <Footer />
    </div>
  );
};

export default USStudentVisaPage;
